/**
 * useReplaySession — one open replay tab's data + cursor + playback state.
 *
 * The fetching strategy is WINDOWED, never whole-run: the run's bounds come
 * from the lightweight `/window` probe, and tick payloads load in
 * ~2000-step windows around the cursor (cached per window, in-flight
 * deduped). Scrubbing into an unloaded window shows a loading frame, loads
 * that window, then SNAPS the cursor to the nearest recorded tick — a
 * recording can have gaps (dataset appends are best-effort), and the
 * replay never fabricates a tick that wasn't recorded.
 *
 * Playback advances one RECORDED tick per beat at the chosen rate and
 * prefetches the next window near the loaded edge. It is entirely local:
 * no WS, no run-state, no engine — a live run may keep ticking beside it.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { ReplayRange, ReplayRunInfo, ReplayTickRow } from './types';
import {
  REPLAY_WINDOW,
  nearestStep,
  stepByRecorded,
  windowBounds,
  windowIndexFor,
} from './logic';

/** The fetch surface the session needs (RemoteProjectAdapter implements
 *  this; tests pass a stub). */
export interface ReplayFetch {
  getDatasetWindow(projectId: string, runId: string): Promise<ReplayRunInfo>;
  getDatasetTicks(
    projectId: string,
    runId: string,
    opts: { from: number; to: number; limit: number },
  ): Promise<ReplayTickRow[]>;
}

export interface ReplaySession {
  info: ReplayRunInfo | null;
  /** Initial bounds fetch in flight. */
  loading: boolean;
  /** Hard error (fetch failure) — surfaced as a banner. */
  error: string | null;
  /** The run id no longer resolves (pruned by `datasetMaxRuns`) — the
   *  degraded "source pruned" state. */
  sourcePruned: boolean;
  /** All loaded rows, step-ascending (windows merged). */
  orderedRows: ReplayTickRow[];
  steps: number[];
  /** Loaded windows as [from, to] step spans (the timeline's shading —
   *  unshaded track is unrecorded/not-yet-fetched territory). */
  loadedSpans: Array<{ from: number; to: number }>;
  /** The current target step (a scrub may land between recorded ticks
   *  until the window loads and snaps it). */
  cursor: number | null;
  /** The exact row at the cursor, or null while its window loads / the
   *  cursor sits in a gap larger than a window. */
  cursorRow: ReplayTickRow | null;
  playing: boolean;
  rate: number;
  range: ReplayRange;
  /** Scrub to a step (clamped to range; loads + snaps async). */
  setCursor: (step: number) => void;
  /** Move by N recorded ticks (negative backwards). */
  move: (delta: number) => void;
  play: () => void;
  pause: () => void;
  setRate: (rate: number) => void;
  /** Narrow the viewing range (the timeline's range brush). */
  setRange: (range: ReplayRange) => void;
}

export function useReplaySession(
  fetcher: ReplayFetch,
  projectId: string,
  runId: string,
  initialRange?: ReplayRange,
): ReplaySession {
  const [info, setInfo] = useState<ReplayRunInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sourcePruned, setSourcePruned] = useState(false);
  const [cursor, setCursorState] = useState<number | null>(null);
  const [playing, setPlaying] = useState(false);
  const [rate, setRate] = useState(4);
  const [range, setRangeState] = useState<ReplayRange | null>(null);
  const [version, setVersion] = useState(0);

  // Window cache. Anchored at info.firstStep (see windowIndexFor); loaded +
  // in-flight are separate maps so a scrub into a loading window dedups.
  const loaded = useRef<Map<number, ReplayTickRow[]>>(new Map());
  const inflight = useRef<Map<number, Promise<ReplayTickRow[]>>>(new Map());

  // ── Bounds probe ────────────────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    loaded.current.clear();
    inflight.current.clear();
    setLoading(true);
    setError(null);
    setSourcePruned(false);
    setInfo(null);
    setCursorState(null);
    setPlaying(false);
    setRangeState(null);
    fetcher
      .getDatasetWindow(projectId, runId)
      .then((res) => {
        if (!alive) return;
        setInfo(res);
        const first = res.firstStep ?? 0;
        const last = res.lastStep ?? first;
        const init = initialRange
          ? { from: Math.max(first, initialRange.from), to: Math.min(last, initialRange.to) }
          : { from: first, to: last };
        setRangeState(init.from <= init.to ? init : { from: first, to: last });
        setCursorState(init.from);
      })
      .catch((e: unknown) => {
        if (!alive) return;
        const msg = e instanceof Error ? e.message : String(e);
        if (/not found|no dataset run|404/i.test(msg)) setSourcePruned(true);
        else setError(msg);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
    // initialRange is an open-time seed only — re-probing on identity
    // changes would reset a live session on every owner re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetcher, projectId, runId]);

  const ensureWindow = useCallback(
    (index: number): void => {
      if (info === null || loaded.current.has(index) || inflight.current.has(index)) return;
      const { from, to } = windowBounds(info.firstStep ?? 0, index);
      const p = fetcher
        .getDatasetTicks(projectId, runId, { from, to, limit: REPLAY_WINDOW })
        .then((rows) => {
          loaded.current.set(index, rows);
          inflight.current.delete(index);
          setVersion((v) => v + 1);
          return rows;
        })
        .catch((e: unknown) => {
          inflight.current.delete(index);
          const msg = e instanceof Error ? e.message : String(e);
          if (/not found|no dataset run|404/i.test(msg)) setSourcePruned(true);
          else setError(msg);
          return [];
        });
      inflight.current.set(index, p);
    },
    [fetcher, info, projectId, runId],
  );

  const orderedRows = useMemo(() => {
    void version;
    const all: ReplayTickRow[] = [];
    const indexes = [...loaded.current.keys()].sort((a, b) => a - b);
    for (const i of indexes) all.push(...(loaded.current.get(i) ?? []));
    return all;
  }, [version]);

  const steps = useMemo(() => orderedRows.map((r) => Number(r.step)), [orderedRows]);

  const loadedSpans = useMemo(() => {
    void version;
    if (info === null) return [];
    const first = info.firstStep ?? 0;
    return [...loaded.current.keys()]
      .sort((a, b) => a - b)
      .map((i) => windowBounds(first, i));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info, version]);

  // ── Cursor window loading + gap snapping ────────────────────────────────
  useEffect(() => {
    if (info === null || cursor === null) return;
    ensureWindow(windowIndexFor(cursor, info.firstStep ?? 0));
  }, [cursor, info, ensureWindow, version]);

  // Once the cursor's window is loaded, snap an off-tick cursor to the
  // nearest RECORDED step within that window (a scrub may have landed in a
  // gap). Runs on version bumps so a late-loading window still snaps.
  useEffect(() => {
    if (info === null || cursor === null) return;
    const index = windowIndexFor(cursor, info.firstStep ?? 0);
    const rows = loaded.current.get(index);
    if (rows === undefined) return; // still loading — the load effect owns it
    const exact = rows.some((r) => Number(r.step) === cursor);
    if (!exact) {
      const win = windowBounds(info.firstStep ?? 0, index);
      const near = nearestStep(
        rows.map((r) => Number(r.step)),
        cursor,
      );
      // Only snap INSIDE the window — a far jump across a gap would be a
      // surprising scrub correction.
      if (near !== null && near >= win.from && near <= win.to) setCursorState(near);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor, info, version]);

  const setCursor = useCallback(
    (step: number) => {
      setPlaying(false);
      if (range === null) setCursorState(step);
      else setCursorState(Math.max(range.from, Math.min(range.to, step)));
    },
    [range],
  );

  const move = useCallback(
    (delta: number) => {
      if (range === null || cursor === null || steps.length === 0) return;
      const next = stepByRecorded(steps, cursor, delta, range);
      if (next === null) {
        // At the loaded edge — pull the adjacent window in and stay put
        // this beat (playback resumes once it lands).
        const dir = delta > 0 ? 1 : -1;
        const { from, to } = windowBounds(
          info?.firstStep ?? 0,
          windowIndexFor(cursor, info?.firstStep ?? 0) + dir,
        );
        const target = dir > 0 ? to + 1 : from - 1;
        ensureWindow(windowIndexFor(Math.max(0, target), info?.firstStep ?? 0));
        return;
      }
      setCursorState(next);
    },
    [cursor, ensureWindow, info, range, steps],
  );

  // ── Playback ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!playing) return;
    const interval = window.setInterval(() => move(1), Math.max(40, 1000 / rate));
    return () => window.clearInterval(interval);
  }, [playing, rate, move]);

  // Stop at the range end (the interval's clamp leaves the cursor pinned;
  // an explicit stop keeps the ▶ button honest).
  useEffect(() => {
    if (playing && range !== null && cursor !== null && cursor >= range.to) setPlaying(false);
  }, [playing, range, cursor]);

  const play = useCallback(() => {
    if (range === null) return;
    // Play from the current cursor, or the range start when pinned at the end.
    if (cursor !== null && cursor >= range.to) setCursorState(range.from);
    setPlaying(true);
  }, [cursor, range]);

  const pause = useCallback(() => setPlaying(false), []);

  const setRange = useCallback(
    (next: ReplayRange) => {
      setRangeState(next);
      setCursorState((cur) => Math.max(next.from, Math.min(next.to, cur ?? next.from)));
    },
    [],
  );

  const cursorRow = useMemo(() => {
    if (cursor === null) return null;
    const rows = loaded.current.get(windowIndexFor(cursor, info?.firstStep ?? 0));
    return rows?.find((r) => Number(r.step) === cursor) ?? null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor, info, version]);

  return {
    info,
    loading,
    error,
    sourcePruned,
    orderedRows,
    steps,
    loadedSpans,
    cursor,
    cursorRow,
    playing,
    rate,
    range: range ?? { from: 0, to: 0 },
    setCursor,
    move,
    play,
    pause,
    setRate,
    setRange,
  };
}
